import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { useAuth } from "../components/AuthContext";
import { db } from "../services/firebase";

const ViewQuotation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(null);
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!user || !id) return;

    const fetchQuotation = async () => {
      try {
        const snap = await getDoc(doc(db, "users", user.uid, "quotations", id));

        if (snap.exists()) {
          setQuotation(snap.data());
        } else {
          console.warn("Quotation not found");
        }
      } catch (err) {
        console.error("Failed to load Quotation:", err);
      }
    };

    fetchQuotation();
  }, [user, id]);

  if (loading || !user || !quotation) {
    return <p style={{ padding: 24 }}>Loading...</p>;
  }

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Quotation Details</h2>
          <p style={styles.subtitle}>View only mode</p>
        </div>

        <button style={styles.backBtn} onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>

      {/* MAIN CARD */}
      <div style={styles.card}>
        {/* TOP GRID */}
        <div style={styles.grid}>
          <Info label="Quotation Number" value={quotation.quotationNumber} />
          <Info label="Company" value={quotation.companyName} />
          <Info
            label="Date"
            value={quotation.createdAt?.toDate().toLocaleDateString()}
          />
          <Info
            label="Status"
            value={
              <span
                style={{
                  ...styles.statusBadge,
                  background:
                    quotation.status === "FINAL" ? "#dcfce7" : "#fef9c3",
                  color: quotation.status === "FINAL" ? "#166534" : "#854d0e",
                }}
              >
                {quotation.status || "DRAFT"}
              </span>
            }
          />
        </div>

        {/* ADDRESS SECTION */}
        <div style={styles.addressRow}>
          <div style={styles.addressBox}>
            <h4 style={styles.sectionTitle}>From</h4>
            <p style={styles.addressText}>{quotation.fromAddress}</p>
          </div>

          <div style={styles.addressBox}>
            <h4 style={styles.sectionTitle}>To</h4>
            <p style={styles.addressText}>{quotation.toAddress}</p>
          </div>
        </div>

        {/* ITEMS TABLE */}
        <div style={{ marginTop: 24 }}>
          <h3 style={styles.sectionTitle}>Items</h3>

          <table style={styles.table}>
            <thead>
              <tr style={styles.theadRow}>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>HSN</th>
                <th style={styles.th}>Qty</th>
                <th style={styles.th}>Rate</th>
                <th style={styles.th}>Unit</th>
                <th style={styles.th}>Total</th>
              </tr>
            </thead>

            <tbody>
              {quotation.items?.map((item, idx) => (
                <tr key={idx}>
                  <td style={styles.td}>{item.desc}</td>
                  <td style={styles.td}>{item.hsn}</td>
                  <td style={styles.td}>{item.qty}</td>
                  <td style={styles.td}>₹{item.rate}</td>
                  <td style={styles.td}>{item.unit}</td>
                  <td style={styles.td}>
                    ₹{item.total || item.qty * item.rate}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ✅ PAYMENT CONFIRMATION */}
        <div style={styles.paymentCard}>
          <div style={styles.paymentHeader}>
            <h3 style={styles.sectionTitle}>Payment Status</h3>

            <span
              style={{
                ...styles.paymentBadge,
                background:
                  (quotation.payment || "Pending") === "Success"
                    ? "#dcfce7"
                    : "#fef9c3",
                color:
                  (quotation.payment || "Pending") === "Success"
                    ? "#166534"
                    : "#854d0e",
                border:
                  (quotation.payment || "Pending") === "Success"
                    ? "1px solid #22c55e"
                    : "1px solid #facc15",
              }}
            >
              {(quotation.payment || "Pending").toUpperCase()}
            </span>
          </div>

          <div style={styles.paymentInfoGrid}>
            <div style={styles.paymentInfoBox}>
              <p style={styles.paymentLabel}>Payment State</p>
              <p style={styles.paymentValue}>
                {quotation.payment || "Pending"}
              </p>
            </div>

            <div style={styles.paymentInfoBox}>
              <p style={styles.paymentLabel}>Grand Total</p>
              <p style={styles.paymentAmount}>
                ₹{quotation.grandTotal?.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* TOTAL BOX */}
        <div style={styles.totalBox}>
          <div style={styles.totalRow}>
            <span>GST ({quotation.gstPercent}%)</span>
            <strong>₹{quotation.gstAmount?.toFixed(2)}</strong>
          </div>

          <div style={{ ...styles.totalRow, ...styles.grandTotal }}>
            <span>Grand Total</span>
            <strong>₹{quotation.grandTotal?.toFixed(2)}</strong>
          </div>
        </div>

        {/* NOTES */}
        {quotation.notes && (
          <div style={styles.notesBox}>
            <h4 style={styles.sectionTitle}>Notes</h4>
            <p style={{ color: "#475569" }}>{quotation.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div style={styles.infoBox}>
    <p style={styles.infoLabel}>{label}</p>
    <p style={styles.infoValue}>{value || "-"}</p>
  </div>
);

const styles = {
  page: {
    padding: "36px",
    background:
      "linear-gradient(180deg, #f0f6ff 0%, #f8fafc 40%, #ffffff 100%)",
    minHeight: "100vh",
    fontFamily: "Inter, system-ui",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 28,
    padding: "18px 22px",
    borderRadius: 16,
    background:
      "linear-gradient(135deg, rgba(37,99,235,0.08), rgba(59,130,246,0.12))",
    border: "1px solid rgba(37,99,235,0.15)",
  },

  title: {
    fontSize: 28,
    fontWeight: 800,
    color: "#1e3a8a",
  },

  subtitle: {
    color: "#64748b",
    fontSize: 14,
  },

  backBtn: {
    padding: "10px 18px",
    borderRadius: 10,
    border: "1px solid #c7d2fe",
    background: "white",
    cursor: "pointer",
    fontWeight: 600,
    color: "#1d4ed8",
  },

  card: {
    background: "rgba(255,255,255,0.9)",
    borderRadius: 22,
    padding: 30,
    boxShadow: "0 25px 60px rgba(2,6,23,0.08)",
    border: "1px solid #e0e7ff",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
    gap: 20,
    marginBottom: 26,
  },

  infoBox: {
    background: "linear-gradient(135deg, #ffffff, #f8fbff)",
    padding: 16,
    borderRadius: 14,
    border: "1px solid #e6edff",
  },

  infoLabel: {
    fontSize: 12,
    color: "#64748b",
    marginBottom: 6,
    fontWeight: 600,
  },

  infoValue: {
    fontSize: 15,
    fontWeight: 700,
    color: "#0f172a",
  },

  addressRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 22,
    marginTop: 14,
  },

  addressBox: {
    background: "linear-gradient(135deg,#ffffff,#f8fafc)",
    padding: 18,
    borderRadius: 16,
    border: "1px solid #e2e8f0",
  },

  addressText: {
    color: "#334155",
    whiteSpace: "pre-line",
    lineHeight: 1.5,
    fontSize: 14,
  },

  sectionTitle: {
    fontSize: 17,
    fontWeight: 700,
    marginBottom: 12,
    color: "#1e293b",
  },

  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    marginTop: 6,
    overflow: "hidden",
    borderRadius: 14,
    border: "1px solid #e2e8f0",
  },

  theadRow: {
    background: "linear-gradient(135deg,#f1f5f9,#eef2ff)",
  },

  th: {
    padding: 14,
    fontSize: 12.5,
    color: "#475569",
    textAlign: "center",
    fontWeight: 700,
    borderBottom: "1px solid #e2e8f0",
  },

  td: {
    padding: 14,
    borderBottom: "1px solid #f1f5f9",
    textAlign: "center",
    fontSize: 14,
    color: "#0f172a",
    background: "#ffffff",
  },

  paymentCard: {
    marginTop: 26,
    background: "linear-gradient(135deg,#ffffff,#f8fbff)",
    padding: 20,
    borderRadius: 18,
    border: "1px solid #e0e7ff",
  },

  paymentHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },

  paymentBadge: {
    padding: "6px 14px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
  },

  paymentInfoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
    gap: 16,
  },

  paymentInfoBox: {
    background: "#ffffff",
    padding: 14,
    borderRadius: 12,
    border: "1px solid #e2e8f0",
  },

  paymentLabel: {
    fontSize: 12,
    color: "#64748b",
    marginBottom: 4,
    fontWeight: 600,
  },

  paymentValue: {
    fontSize: 15,
    fontWeight: 700,
    color: "#0f172a",
  },

  paymentAmount: {
    fontSize: 18,
    fontWeight: 800,
    color: "#1d4ed8",
  },

  totalBox: {
    marginTop: 26,
    marginLeft: "auto",
    width: 280,
    background: "linear-gradient(135deg,#ffffff,#f8fbff)",
    padding: 18,
    borderRadius: 16,
    border: "1px solid #e0e7ff",
  },

  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 10,
    color: "#334155",
    fontSize: 14,
    fontWeight: 500,
  },

  grandTotal: {
    fontSize: 20,
    fontWeight: 800,
    color: "#1d4ed8",
    borderTop: "1px solid #e2e8f0",
    paddingTop: 10,
  },

  statusBadge: {
    padding: "5px 12px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
  },

  notesBox: {
    marginTop: 26,
    background: "linear-gradient(135deg,#ffffff,#f8fafc)",
    padding: 18,
    borderRadius: 16,
    border: "1px solid #e2e8f0",
  },
};

export default ViewQuotation;
